import { createFileRoute } from '@tanstack/react-router';
import * as React from 'react';
import { useTranslations } from 'use-intl';

import { PageHeader } from '@/components/dashboard/page-header';
import { DisputePanelClient } from '@/components/dispute/dispute-panel-client';
import { trpc } from '@/lib/trpc/client';

export const Route = createFileRoute('/_app/projects/$id/dispute')({
  component: DisputePage,
});

function DisputePage(): React.ReactElement {
  const { id } = Route.useParams();
  const t = useTranslations('dispute');
  const meQuery = trpc.user.me.useQuery();
  const projectQuery = trpc.project.byId.useQuery({ projectId: id });
  const isAdminUser = meQuery.data?.isAdmin === true && meQuery.data?.status === 'ACTIVE';
  const participantsQuery = trpc.dispute.participantsByProject.useQuery(
    { projectId: id },
    { enabled: isAdminUser },
  );

  if (meQuery.isLoading || projectQuery.isLoading) {
    return (
      <div className="flex flex-col gap-6">
        <PageHeader title={t('pageTitle')} subtitle={t('pageSubtitle')} />
        <p className="text-muted-foreground text-sm">{t('loading')}</p>
      </div>
    );
  }
  if (projectQuery.isError || !projectQuery.data || !meQuery.data) {
    return (
      <div className="flex flex-col gap-6">
        <PageHeader title={t('pageTitle')} subtitle={t('pageSubtitle')} />
        <div className="border-border bg-card/50 rounded-2xl border border-dashed p-10 text-center">
          <p className="text-muted-foreground text-sm">{t('error')}</p>
        </div>
      </div>
    );
  }

  const project = projectQuery.data;
  const me = meQuery.data;
  const isAdmin = me.isAdmin === true && me.status === 'ACTIVE';
  const isParticipant = me.id === project.requesterId || me.id === project.bidderId;
  if (!isParticipant && !isAdmin) {
    return (
      <div className="flex flex-col gap-6">
        <PageHeader title={t('pageTitle')} subtitle={t('pageSubtitle')} />
        <div className="border-border bg-card/50 rounded-2xl border border-dashed p-10 text-center">
          <p className="text-muted-foreground text-sm">{t('error')}</p>
        </div>
      </div>
    );
  }

  const requester = isAdmin
    ? (participantsQuery.data?.requester ?? { id: project.requesterId, label: project.requesterId })
    : { id: project.requesterId, label: project.requesterId };
  const bidder = isAdmin
    ? (participantsQuery.data?.bidder ?? { id: project.bidderId, label: project.bidderId })
    : { id: project.bidderId, label: project.bidderId };

  return (
    <div className="flex flex-col gap-6">
      <PageHeader title={t('pageTitle')} subtitle={t('pageSubtitle')} />
      <DisputePanelClient
        projectId={project.id}
        currentUserId={me.id}
        isAdmin={isAdmin}
        requester={requester}
        bidder={bidder}
      />
    </div>
  );
}
